if (!customElements.get('lua-personalized-product')) {
  customElements.define('lua-personalized-product', class extends HTMLElement {
    connectedCallback() {
      if (this.initFrame) return;
      this.initFrame = requestAnimationFrame(() => {
        this.initFrame = null;
        if (this.isConnected) this.initialize();
      });
    }
    initialize() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.form = this.querySelector('.lpr-form');
      this.variantSelect = this.querySelector('[data-variant-select]');
      this.nameInput = this.querySelector('[data-personalization-name]');
      this.quantity = this.querySelector('[data-quantity]');
      this.submit = this.querySelector('[data-product-submit]');
      this.label = this.querySelector('[data-submit-label]');
      this.message = this.querySelector('[data-product-message]');
      this.galleryPanels = [...this.querySelectorAll('[data-media-panel]')];
      this.galleryIndex = this.galleryPanels.findIndex(panel => !panel.hidden);
      if (this.galleryIndex < 0 && this.galleryPanels.length) this.showMedia(this.galleryPanels[0].dataset.mediaPanel);
      const toolbar = this.querySelector('[data-gallery-toolbar]');
      if (toolbar) toolbar.hidden = this.galleryPanels.length < 2;
      const stage = this.querySelector('.lpr-stage');
      if (stage && this.galleryPanels.length > 1) {
        stage.tabIndex = 0;
        stage.setAttribute('aria-label', 'Product gallery. Use left and right arrow keys to browse.');
      }
      this.updateGalleryCount();
      try {
        const data = JSON.parse(this.querySelector('[data-product-data]')?.textContent || '{}');
        this.variants = (Array.isArray(data.variants) ? data.variants : []).map(variant => ({
          ...variant, options: (variant.options || []).map(value => String(value?.name ?? value))
        }));
      } catch { this.variants = []; }
      this.addEventListener('click', event => {
        const thumb = event.target.closest('[data-media-choice]');
        if (thumb && this.contains(thumb)) this.showMedia(thumb.dataset.mediaChoice, true);
        const arrow = event.target.closest('[data-gallery-step]');
        if (arrow && this.contains(arrow)) this.stepGallery(Number(arrow.dataset.galleryStep));
        const change = event.target.closest('[data-quantity-change]');
        if (change && this.contains(change) && this.quantity) {
          const step = Number(this.quantity.step) || 1;
          this.quantity.value = String(Number(this.quantity.value || this.quantity.min || 1) + Number(change.dataset.quantityChange) * step);
          this.normalizeQuantity();
        }
      }, {signal});
      this.addEventListener('keydown', event => {
        if (!event.target.closest('.lpr-gallery') || this.galleryPanels.length < 2) return;
        let index;
        if (event.key === 'ArrowRight') index = (Math.max(0, this.galleryIndex) + 1) % this.galleryPanels.length;
        else if (event.key === 'ArrowLeft') index = (Math.max(0, this.galleryIndex) - 1 + this.galleryPanels.length) % this.galleryPanels.length;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = this.galleryPanels.length - 1;
        else return;
        event.preventDefault();
        this.showMedia(this.galleryPanels[index].dataset.mediaPanel, true);
        if (event.target.closest('[data-media-choice]')) [...this.querySelectorAll('[data-media-choice]')][index]?.focus({preventScroll: true});
      }, {signal});
      this.addEventListener('change', event => {
        if (event.target.matches('[data-option-position]')) this.updateVariant(true);
        if (event.target === this.quantity) this.normalizeQuantity();
        if (event.target === this.variantSelect) {
          const selected = this.variants.find(v => String(v.id) === this.variantSelect.value);
          if (selected) this.querySelectorAll('[data-option-position]').forEach(input => {
            input.checked = selected.options[Number(input.dataset.optionPosition) - 1] === input.value;
          });
          this.updateVariant(true);
        }
      }, {signal});
      this.nameInput?.addEventListener('input', () => this.nameInput.setCustomValidity(''), {signal});
      this.form?.addEventListener('submit', event => {
        if (this.dataset.canPurchase !== 'true' || !this.variants.length) { event.preventDefault(); return; }
        this.updateVariant(false);
        if (this.nameInput) {
          this.nameInput.value = this.nameInput.value.trim();
          this.nameInput.setCustomValidity(this.nameInput.required && !this.nameInput.value ? 'Please enter the personalization text for this item.' : '');
        }
        this.normalizeQuantity();
        if (!this.currentVariant?.available || this.currentVariant.requiresPlan || !this.form.checkValidity()) {
          event.preventDefault(); this.form.reportValidity(); return;
        }
        this.submit.disabled = true;
        this.label.textContent = 'Adding to cart…';
      }, {signal});
      window.addEventListener('pageshow', () => this.updateVariant(false), {signal});
      if (this.dataset.canPurchase === 'true' && this.variantSelect && this.variants.length) {
        this.dataset.optionsReady = 'true'; this.updateVariant(false);
      } else delete this.dataset.optionsReady;
    }
    disconnectedCallback() {
      if (this.initFrame) cancelAnimationFrame(this.initFrame);
      this.initFrame = null; this.controller?.abort();
    }
    selectedOptions() {
      const options = [];
      this.querySelectorAll('[data-option-position]:checked').forEach(input => { options[Number(input.dataset.optionPosition) - 1] = input.value; });
      return options;
    }
    updateVariant(changeUrlAndPhoto) {
      if (!this.variantSelect || !this.variants.length) return;
      const options = this.selectedOptions();
      const variant = options.length
        ? this.variants.find(v => v.options.length === options.length && v.options.every((value, i) => value === options[i]))
        : this.variants.find(v => String(v.id) === this.variantSelect.value);
      this.currentVariant = variant || null;
      this.variantSelect.value = variant ? String(variant.id) : '';
      const available = Boolean(variant?.available && !variant.requiresPlan);
      if (this.submit) this.submit.disabled = !available;
      if (this.label) this.label.textContent = !variant ? 'Unavailable combination' : variant.requiresPlan ? 'Purchase plan required' : available ? 'Add to cart' : 'Sold out';
      if (this.message) this.message.textContent = !variant ? 'This combination is not available. Please choose another option.'
        : variant.requiresPlan ? 'This variant requires a purchase plan. Use the original purchase-plan template.'
        : available ? '' : 'This option is currently sold out.';
      const price = this.querySelector('[data-product-price]');
      if (price) price.textContent = variant ? variant.priceText : 'Choose an available combination';
      const compare = this.querySelector('[data-compare-price]');
      if (compare) { compare.hidden = !variant?.onSale; compare.textContent = variant?.compareText || ''; }
      this.querySelectorAll('[data-option-label]').forEach(el => { el.textContent = options[Number(el.dataset.optionLabel) - 1] || ''; });
      this.querySelectorAll('[data-option-position]').forEach(input => {
        const position = Number(input.dataset.optionPosition) - 1;
        const possible = this.variants.some(v => v.available && !v.requiresPlan && v.options.every((value, i) => value === (i === position ? input.value : options[i])));
        input.closest('.lpr-option').dataset.unavailable = String(!possible);
        input.setAttribute('aria-label', input.value + (possible ? '' : ' — unavailable with current selections'));
      });
      if (variant && this.quantity) {
        const rules = variant.quantityRule || {};
        this.quantity.min = String(rules.min || 1); this.quantity.step = String(rules.increment || 1);
        if (rules.max != null) this.quantity.max = String(rules.max); else this.quantity.removeAttribute('max');
        this.normalizeQuantity();
        const note = this.querySelector('[data-quantity-note]');
        if (note) note.textContent = rules.min > 1 || rules.increment > 1 || rules.max != null
          ? `Minimum ${rules.min || 1}${rules.max != null ? ', maximum ' + rules.max : ''}; increments of ${rules.increment || 1}.` : '';
      }
      if (changeUrlAndPhoto) {
        if (variant) this.showAssignedVariantMedia(variant);
        const url = new URL(window.location.href); url.searchParams.delete('option_values');
        if (variant) url.searchParams.set('variant', String(variant.id)); else url.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', url);
      }
    }
    normalizeQuantity() {
      if (!this.quantity) return;
      const min = Number(this.quantity.min) || 1, step = Number(this.quantity.step) || 1;
      const maximum = this.quantity.hasAttribute('max') ? Number(this.quantity.max) : Infinity;
      const maxAligned = Number.isFinite(maximum) ? min + Math.floor((maximum - min) / step) * step : Infinity;
      const raw = Number(this.quantity.value);
      const value = Number.isFinite(raw) && raw > 0 ? min + Math.ceil((raw - min) / step) * step : min;
      this.quantity.value = String(Math.max(min, Math.min(maxAligned, value)));
      const minus = this.querySelector('[data-quantity-change="-1"]'), plus = this.querySelector('[data-quantity-change="1"]');
      if (minus) minus.disabled = Number(this.quantity.value) <= min;
      if (plus) plus.disabled = Number(this.quantity.value) >= maxAligned;
    }
    stopHiddenMedia(panel) {
      panel.querySelectorAll('video').forEach(video => video.pause());
      panel.querySelectorAll('iframe').forEach(frame => { const src = frame.getAttribute('src'); if (src) frame.setAttribute('src', src); });
      panel.querySelector('model-viewer')?.pause?.();
    }
    updateGalleryCount() {
      const count = this.querySelector('[data-gallery-count]');
      if (count) count.textContent = this.galleryIndex >= 0 ? `${this.galleryIndex + 1} / ${this.galleryPanels.length}` : 'Variant view';
    }
    stepGallery(step) {
      if (!this.galleryPanels?.length) return;
      const index = this.galleryIndex < 0 ? (step > 0 ? 0 : this.galleryPanels.length - 1)
        : (this.galleryIndex + step + this.galleryPanels.length) % this.galleryPanels.length;
      this.showMedia(this.galleryPanels[index].dataset.mediaPanel, true);
    }
    showMedia(id, revealThumbnail = false) {
      id = String(id);
      const panels = this.galleryPanels || [...this.querySelectorAll('[data-media-panel]')];
      const index = panels.findIndex(panel => panel.dataset.mediaPanel === id);
      if (index < 0) return false;
      panels.forEach((panel, i) => {
        const hiding = i !== index;
        if (hiding && !panel.hidden) this.stopHiddenMedia(panel);
        panel.hidden = hiding;
        if (!hiding) panel.querySelectorAll('img').forEach(img => img.loading = 'eager');
      });
      const extra = this.querySelector('[data-variant-photo]'); if (extra) extra.hidden = true;
      this.galleryIndex = index;
      this.querySelectorAll('[data-media-choice]').forEach(button => {
        const selected = button.dataset.mediaChoice === id; button.setAttribute('aria-pressed', String(selected));
        if (selected && revealThumbnail) {
          const strip = button.parentElement;
          if (strip.scrollWidth > strip.clientWidth) strip.scrollLeft = Math.max(0, button.offsetLeft - strip.offsetLeft - (strip.clientWidth - button.offsetWidth) / 2);
        }
      });
      this.updateGalleryCount();
      const status = this.querySelector('[data-gallery-status]'); if (status) status.textContent = `Product image ${index + 1} of ${panels.length}.`;
      return true;
    }
    imageSource(image) {
      const raw = typeof image === 'string' ? image : image?.src || image?.url || '';
      if (!raw) return '';
      try { const u = new URL(raw, window.location.href); return ['http:', 'https:'].includes(u.protocol) ? u.href : ''; }
      catch { return ''; }
    }
    showAssignedVariantMedia(variant) {
      if (variant.mediaId && this.showMedia(variant.mediaId, true)) return;
      const src = this.imageSource(variant.image);
      if (src) {
        const matching = this.galleryPanels.find(panel => [...panel.querySelectorAll('img')].some(img => img.src === src || img.currentSrc === src));
        if (matching && this.showMedia(matching.dataset.mediaPanel, true)) return;
        if (this.showVariantImage(variant.image)) return;
      }
      if (this.galleryPanels.length) this.showMedia(this.galleryPanels[0].dataset.mediaPanel, true);
      const status = this.querySelector('[data-gallery-status]');
      if (status) status.textContent = 'No separate image is assigned to this variant. Showing the product gallery.';
    }
    showVariantImage(image) {
      const panel = this.querySelector('[data-variant-photo]'), src = this.imageSource(image);
      if (!panel || !src) return false;
      this.galleryPanels.forEach(item => { if (!item.hidden) this.stopHiddenMedia(item); item.hidden = true; });
      let img = panel.querySelector('img');
      if (!img) { img = document.createElement('img'); img.className = 'lpr-photo'; panel.appendChild(img); }
      img.src = src;
      img.alt = (typeof image === 'object' && image.alt) || this.querySelector('h1')?.textContent || 'Selected product variant';
      img.width = image.width || 1200; img.height = image.height || 1200; img.loading = 'eager';
      panel.hidden = false; this.galleryIndex = -1;
      this.querySelectorAll('[data-media-choice]').forEach(button => button.setAttribute('aria-pressed', 'false'));
      this.updateGalleryCount();
      const status = this.querySelector('[data-gallery-status]'); if (status) status.textContent = 'Showing the selected variant image.';
      return true;
    }
  });
}
